import { Search } from "lucide-react";
import { Tooltip } from "radix-ui";
import { useCommandMenu } from "@/shared/components/command-menu/command-menu.context";
import { useSidebarCollapsed } from "@/shared/hooks/use-sidebar-collapsed";
import { cn } from "@/shared/lib/cn";

/// Sidebar entry point for the command menu. It looks like a search field but
/// is a button: typing happens inside the dialog, so a real input here would
/// only have to hand its value over on open.
///
/// A keyboard-only palette is invisible to anyone who was not told it exists,
/// which is why this exists alongside the Cmd+K listener.
export function SidebarSearchTrigger() {
  const { collapsed } = useSidebarCollapsed();
  const { setOpen } = useCommandMenu();

  const shared = cn(
    "text-sm outline-none transition-colors duration-150",
    "text-text-tertiary hover:text-foreground focus-visible:shadow-ring-focus",
  );

  // Collapsed rail: `px-2` and no centring, matching SidebarNav. Centring the
  // 36px button inside the 68px rail puts its icon 7.5px right of every nav
  // icon, which reads as one row out of line.
  if (collapsed) {
    return (
      <div className="px-2 py-2">
        <Tooltip.Root>
          <Tooltip.Trigger asChild>
            <button
              type="button"
              onClick={() => setOpen(true)}
              aria-label="Search"
              aria-keyshortcuts="Meta+K"
              className={cn(shared, "grid size-9 place-items-center rounded-md hover:bg-muted")}
            >
              <Search className="size-4" />
            </button>
          </Tooltip.Trigger>
          <Tooltip.Portal>
            <Tooltip.Content
              side="right"
              sideOffset={10}
              className="z-50 rounded-md bg-zinc-900 px-2 py-1 text-2xs font-medium text-white shadow-md dark:bg-zinc-100 dark:text-zinc-900"
            >
              Search (⌘K)
            </Tooltip.Content>
          </Tooltip.Portal>
        </Tooltip.Root>
      </div>
    );
  }

  return (
    <div className="px-3 py-2">
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Search"
        aria-keyshortcuts="Meta+K"
        className={cn(
          shared,
          "flex w-full items-center gap-2.5 rounded-md border border-border bg-card px-2.5 py-1.5",
          "hover:border-border-strong",
        )}
      >
        <Search className="size-4 shrink-0" />
        <span className="truncate">Search...</span>
        <kbd className="ml-auto rounded border border-border px-1.5 py-0.5 font-mono text-3xs text-text-quaternary">
          ⌘K
        </kbd>
      </button>
    </div>
  );
}
